import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import type { CustomerRow, OrderHistoryEntry } from '@/Pages/CRM/lib';
import {
  apiError,
  type CreateCustomerAddressDto,
  type CustomerAddress,
  type RepeatOrderDraft,
} from './lib';

const BLANK_ADDRESS: CreateCustomerAddressDto = { line1: '' };

/**
 * The caller's own record — addresses (ADR-0021 §2.4) and order history
 * (already `existing-complete`, per ADR-0021 §1 — `GET /customers/:id`'s
 * `orderHistory`, unchanged by this ADR). "Repeat" (§2.10) hands an unsaved
 * draft up to the order desk; it never creates anything by itself.
 */
export default function CustomerPanel({
  customer,
  onRepeat,
}: {
  customer: CustomerRow;
  onRepeat: (draft: RepeatOrderDraft) => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm().confirm;
  const { format } = useCurrency(null);
  const canManageAddresses = can('customers.manage_addresses');
  // `GET /customers/:id` is `customers.view` — `CALL_CENTER_AGENT`'s seeded
  // role holds it, but a narrower custom role might not, and this call is
  // otherwise unguarded at the point of use. Order history is skipped
  // rather than fired into a guaranteed 403.
  const canViewHistory = can('customers.view');

  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [history, setHistory] = useState<OrderHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [repeatingId, setRepeatingId] = useState<string | null>(null);

  const [addrFormOpen, setAddrFormOpen] = useState(false);
  const [addrForm, setAddrForm] = useState<CreateCustomerAddressDto>(BLANK_ADDRESS);
  const [savingAddr, setSavingAddr] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<CustomerAddress[]>(`/call-center/customers/${customer.id}/addresses`),
      canViewHistory
        ? api.get<{ orderHistory: OrderHistoryEntry[] }>(`/customers/${customer.id}`)
        : Promise.resolve({ data: { orderHistory: [] as OrderHistoryEntry[] } }),
    ])
      .then(([addr, detail]) => {
        setAddresses(addr.data ?? []);
        setHistory(detail.data.orderHistory ?? []);
      })
      .catch((e) => setError(apiError(e, t('callCenter.customer.loadFailed'))))
      .finally(() => setLoading(false));
  }, [customer.id, canViewHistory, t]);

  useEffect(load, [load]);

  const submitAddress = async () => {
    setSavingAddr(true);
    try {
      await api.post(`/call-center/customers/${customer.id}/addresses`, addrForm);
      toast.success(t('callCenter.address.saved'));
      setAddrFormOpen(false);
      setAddrForm(BLANK_ADDRESS);
      load();
    } catch (e) {
      toast.error(apiError(e, t('callCenter.address.saveFailed')));
    } finally {
      setSavingAddr(false);
    }
  };

  const removeAddress = async (address: CustomerAddress) => {
    const ok = await confirm({
      title: t('callCenter.address.removeConfirmTitle'),
      body: t('callCenter.address.removeConfirmBody', { line1: address.line1 }),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/call-center/customers/${customer.id}/addresses/${address.id}`);
      toast.success(t('callCenter.address.removed'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('callCenter.address.removeFailed')));
    }
  };

  const repeat = async (orderId: string) => {
    setRepeatingId(orderId);
    try {
      const { data } = await api.get<RepeatOrderDraft>(`/call-center/orders/${orderId}/repeat`);
      onRepeat(data);
      toast.success(t('callCenter.orderHistory.draftReady'));
    } catch (e) {
      toast.error(apiError(e, t('callCenter.orderHistory.repeatFailed')));
    } finally {
      setRepeatingId(null);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle>{t('callCenter.address.title')}</CardTitle>
          {canManageAddresses && (
            <Button size="sm" variant="outline" onClick={() => setAddrFormOpen(true)}>
              {t('common.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {addresses.length === 0 ? (
            <EmptyState
              title={t('callCenter.address.empty')}
              hint={t('callCenter.address.emptyHint')}
            />
          ) : (
            addresses.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-2 rounded-lg border p-2.5 text-sm">
                <div>
                  <div className="flex items-center gap-2 font-medium">
                    {a.label || t('callCenter.address.unlabelled')}
                    {a.isDefault && <Badge tone="brand">{t('callCenter.address.default')}</Badge>}
                  </div>
                  <div className="text-xs text-ink-muted">
                    {[a.line1, a.line2, a.area, a.city, a.landmark].filter(Boolean).join(', ')}
                  </div>
                </div>
                {canManageAddresses && (
                  <Button size="sm" variant="ghost" onClick={() => removeAddress(a)}>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('callCenter.orderHistory.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {!canViewHistory ? (
            <p className="text-sm text-ink-muted">{t('callCenter.orderHistory.viewDenied')}</p>
          ) : history.length === 0 ? (
            <EmptyState title={t('callCenter.orderHistory.empty')} />
          ) : (
            history.slice(0, 8).map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm">
                <div>
                  <div className="font-medium">{o.number}</div>
                  <div className="text-xs text-ink-muted">
                    {o.fulfillmentType} · {t('callCenter.orderHistory.itemCount', { count: o._count?.items ?? 0 })} ·{' '}
                    <span className="tabular-nums">{format(o.total)}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={repeatingId === o.id}
                  onClick={() => void repeat(o.id)}
                >
                  {t('callCenter.orderHistory.repeat')}
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog
        open={addrFormOpen}
        onClose={() => setAddrFormOpen(false)}
        title={t('callCenter.address.newAddress')}
        footer={
          <>
            <Button variant="outline" onClick={() => setAddrFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitAddress} disabled={savingAddr || !addrForm.line1.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('callCenter.address.label')}>
            <Input
              value={addrForm.label ?? ''}
              onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })}
            />
          </Field>
          <Field label={t('callCenter.address.line1')} required>
            <Input
              value={addrForm.line1}
              onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })}
            />
          </Field>
          <Field label={t('callCenter.address.line2')}>
            <Input
              value={addrForm.line2 ?? ''}
              onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('callCenter.address.area')}>
              <Input
                value={addrForm.area ?? ''}
                onChange={(e) => setAddrForm({ ...addrForm, area: e.target.value })}
              />
            </Field>
            <Field label={t('callCenter.address.city')}>
              <Input
                value={addrForm.city ?? ''}
                onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })}
              />
            </Field>
          </div>
          <Field label={t('callCenter.address.landmark')} hint={t('callCenter.address.landmarkHint')}>
            <Input
              value={addrForm.landmark ?? ''}
              onChange={(e) => setAddrForm({ ...addrForm, landmark: e.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </>
  );
}
